const isEqual = require('lodash/isEqual');
const { DateTime } = require('luxon');

/**
 * The single implementation of a `form.form-condition-rule`, shared by field/group
 * visibility (`getFormDisplayState`) and by conditional email recipients
 * (`Form.js#getConditionalEmail`). Both used to carry their own divergent copy: one was
 * missing the four ordering comparators and had `notEqualTo` inverted, the other had no
 * array support.
 *
 * A rule is `{ field, compare, value, dataType }`, where `dataType` is one of
 * `text` (default) | `number` | `datetime`.
 */

/**
 * `dataType` only makes sense for equality and ordering. Substring and emptiness checks
 * always run against the raw form value, which may legitimately be an array (multi-select,
 * upload) or a boolean (checkbox).
 */
const COERCED_COMPARATORS = new Set([
  'equalTo',
  'notEqualTo',
  'lessThan',
  'lessThanOrEqualTo',
  'greaterThan',
  'greaterThanOrEqualTo',
]);

function coerce(value, dataType) {
  if (dataType === 'number') {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  if (dataType === 'datetime') {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = value instanceof Date
      ? DateTime.fromJSDate(value)
      : DateTime.fromISO(String(value));

    // Compare as millis so the ordering comparators work on plain numbers
    return parsed.isValid ? parsed.toMillis() : null;
  }

  return value;
}

/**
 * Emptiness for the six field types this form engine can produce. lodash's `isEmpty` — used
 * by the previous implementation — reports `true` for every boolean and every number, so an
 * (un)checked checkbox was always "empty".
 */
function isValueEmpty(value) {
  if (value === null || value === undefined || value === '') {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === 'boolean') {
    return value === false;
  }

  if (typeof value === 'number') {
    return false;
  }

  return Object.keys(value).length === 0;
}

/** Ordering is meaningless once either side failed to parse. */
function ordered(compare) {
  return (x, y) => {
    if (x === null || x === undefined || y === null || y === undefined) {
      return false;
    }

    return compare(x, y);
  };
}

const COMPARATORS = {
  equalTo: (x, y) => isEqual(x, y),
  notEqualTo: (x, y) => !isEqual(x, y),
  // Works for both multi-select arrays and plain strings
  contains: (x, y) => (x ?? []).includes(y),
  doesNotContain: (x, y) => !(x ?? []).includes(y),
  startsWith: (x, y) => String(x ?? '').startsWith(y),
  endsWith: (x, y) => String(x ?? '').endsWith(y),
  isEmpty: (x) => isValueEmpty(x),
  isNotEmpty: (x) => !isValueEmpty(x),
  lessThan: ordered((x, y) => x < y),
  lessThanOrEqualTo: ordered((x, y) => x <= y),
  greaterThan: ordered((x, y) => x > y),
  greaterThanOrEqualTo: ordered((x, y) => x >= y),
};

/**
 * @param {*} actualValue The current Formik value of the field the rule points at.
 * @param {{compare?: string, value?: *, dataType?: string}} rule
 * @returns {boolean}
 */
function evaluateRule(actualValue, rule) {
  const compare = rule.compare ?? 'equalTo';
  const comparator = COMPARATORS[compare];

  if (!comparator) {
    console.warn(`[form] Unknown condition comparator "${compare}" — rule ignored.`);
    return false;
  }

  if (!COERCED_COMPARATORS.has(compare)) {
    return comparator(actualValue, rule.value);
  }

  return comparator(
    coerce(actualValue, rule.dataType),
    coerce(rule.value, rule.dataType),
  );
}

module.exports = evaluateRule;
module.exports.COMPARATORS = COMPARATORS;
module.exports.isValueEmpty = isValueEmpty;
